import { EllaProductData } from '@/components/common/EllaCartProduct/EllaCartProduct';
import { z } from 'zod';

export type EllaPromptObject = {
  prompt: string;
  assistant?: string;
};

export const initialPrompts: EllaPromptObject[] = [
  { prompt: 'Build me a look with Vivrelle, REVOLVE, and FWRD' },
  { prompt: 'Help me style a look for an occasion' },
  { prompt: 'Show me what looks are trending' },
  { prompt: 'Help me create a look that matches my style' }
];

export type QDrantProductPayload = {
  id: string;
  updated_title: string;
  brand: string;
  common_color: string;
  image_link: string;
  store: string;
  item_group_id: string;
  category: string;
  sub_category: string;
  sub_sub_category: string;
  description: string;
  updated_description: string;
  retail_price: string;
  current_price: string;
  link: string;
  tier: string;
  subscription_level?: string;
  year: number;
  cj_link?: string;
  gpt_context?: string;
  size?: string;
  product_type: string;
};

export type RecommendationFeedback = {
  id: string;
  recommendationId: string;
  userId?: string;
  isPositive: boolean;
  timestamp: number;
  context: {
    messages: Array<{ role: string; content: string }>;
    recommendations: string[];
    selectedProducts?: string[];
    availableProducts?: any[];
    recommendationPrompt?: string;
  };
};

export type QDrantSearchResult = {
  id: string | number;
  version: number;
  score: number;
  payload?: QDrantProductPayload;
  vector?:
    | Record<string, unknown>
    | number[]
    | number[][]
    | {
        [key: string]:
          | number[]
          | number[][]
          | {
              indices: number[];
              values: number[];
            }
          | undefined;
      }
    | null
    | undefined;
  shard_key?: string | number | Record<string, unknown> | null | undefined;
  order_value?: number | Record<string, unknown> | null | undefined;
};

export interface ProductMemory {
  likedProducts: QDrantProductPayload[];
}

export type Recommendation = {
  id: string;
  brand: string;
  name: string;
  mainImage: string;
  store: string;
  item_group_id: string;
  price: string;
  updated_title: string;
  image_link: string;
  category: string;
  sub_category: string;
  sub_sub_category: string;
  description: string;
  current_price: string;
  link: string;
  cj_link?: string;
  size?: string;
};

/** NOTE: This should directly clone TS type below! */
export const recommendationSchema = z.object({
  id: z.string(),
  brand: z.string(),
  name: z.string(),
  mainImage: z.string(),
  store: z.string(),
  item_group_id: z.string(),
  price: z.string(),
  updated_title: z.string(),
  image_link: z.string(),
  category: z.string(),
  sub_category: z.string().optional(),
  sub_sub_category: z.string().optional(),
  description: z.string(),
  current_price: z.string(),
  link: z.string(),
  size: z.string().optional(),
  year: z.number().optional(),
  gpt_context: z.string().optional(),
  cj_link: z.string(),
  tier: z.string(),
  common_color: z.string(),
  product_type: z.string(),
  retail_price: z.string()
});

/** NOTE: This should directly clone ZOD schema above! */
export type RecommendationSchemaType = {
  id: string;
  brand: string;
  name: string;
  mainImage: string;
  store: string;
  item_group_id: string;
  price: string;
  updated_title: string;
  image_link: string;
  category: string;
  sub_category?: string;
  sub_sub_category?: string;
  description: string;
  current_price: string;
  link: string;
  size?: string;
  year?: number;
  gpt_context?: string;
  cj_link?: string;
  tier?: string;
  common_color?: string;
  product_type?: string;
  retail_price?: string;
};

// The optionals here are to prevent some llm weirdness
export const outputSchema = z.object({
  message: z.string().describe('The message to be sent to the user.'),
  storeFilter: z
    .enum(['both', 'newVivrelle', 'newRevolve', 'skipVivrelle', 'skipRevolve'])
    .optional()
    .describe('The store filter to be used for the recommendations.'),
  products: z
    .array(
      z.object({
        id: z.string(),
        brand: z.string(),
        name: z.string(),
        mainImage: z.string(),
        store: z.string(),
        item_group_id: z.string(),
        price: z.string(),
        updated_title: z.string(),
        image_link: z.string(),
        category: z.string(),
        sub_category: z.string().optional(),
        sub_sub_category: z.string().optional(),
        description: z.string(),
        current_price: z.string(),
        link: z.string(),
        size: z.string().optional(),
        year: z.number().optional(),
        gpt_context: z.string().optional(),
        cj_link: z.string(),
        tier: z.string(),
        common_color: z.string(),
        product_type: z.string(),
        retail_price: z.string()
      })
    )
    .optional()
    .default([]) // Add default empty array for products
    .describe(
      'The products to be recommended to the user. Should only be used for output, not included in the input.'
    )
});

export const PROMPT_SHOW_OTHER_VIVRELLE_ITEMS_COPY =
  'Show me other items available to borrow';
